import { BagTransportTermApiService } from '@/bag/bag-transport-term/bag-transport-term-api.service';
import {
	BagTransportTerm,
	BagTransportTermProduct,
	GenerateReportPayload,
	ProductKindType,
	TermTourismEventSet,
	TermTourismEventSetKinds,
	TourismEventsWithDelay,
	VehicleModel,
} from '@/bag/bag-transport-term/bag-transport-term.model';
import { DateService } from '@/common/date.service';
import { DownloadFileService } from '@/common/download-file.service';
import { EventCategory } from '@/common/enums/EventCategory';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, Inject, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { BagTransportTermChangeRoomAvailabilityComponent } from '../bag-transport-term-change-room-availability/bag-transport-term-change-room-availability.component';
import { NoteListComponent } from '@/note/note-list/note-list.component';
import { TableName } from '@/common/enums/TableName';
import { MatTooltip } from '@angular/material/tooltip';
import { Vehicle } from '@/new-transport/_models/transport.model';
import { TasksListModule } from '@/tasks/tasks-list/tasks-list.module';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { GenericMatAutocompleteComponent } from '@/common/generic-mat-autocomplete/generic-mat-autocomplete.component';
import { environment } from 'src/environments/environment';
import { HttpParams } from '@angular/common/http';
import { TourismEvent } from '@/tourism-events/tourism-events.model';

type ReportType = 'rooming' | 'insurance' | 'facultative';
type ReportKind = 'csv' | 'spreadsheet';
type ReportName = 'RoomingListReport' | 'InsuranceListReport' | 'FacultativeListReport';
const MAX_NAME_LENGTH = 50;

interface MappedTourismEventsKind {
	kind: TermTourismEventSetKinds;
	name: string;
	disable: boolean;
}

enum FileTypeShortcut {
	InsuranceList = 'IL',
	RoomingList = 'RL',
	FacultativeList = 'FL',
}

@Component({
	selector: 'app-bag-transport-term-dialog',
	templateUrl: './bag-transport-term-dialog.component.html',
	styleUrls: ['./bag-transport-term-dialog.component.scss'],
	standalone: true,
	imports: [
		BagTransportTermChangeRoomAvailabilityComponent,
		MatDialogModule,
		MatSelectModule,
		FormsModule,
		CommonModule,
		ReactiveFormsModule,
		MatButtonModule,
		MatInputModule,
		MatRadioModule,
		MatTooltip,
		NoteListComponent,
		TasksListModule,
		MatAutocompleteModule,
		GenericMatAutocompleteComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BagTransportTermDialogComponent implements OnInit {
	chosenEvent;
	vehicles: Vehicle[] | VehicleModel[] = [];
	mappedTourismEventsKinds: MappedTourismEventsKind[] = [
		{ kind: TermTourismEventSetKinds.BOTH, name: 'Wyjazd&Powrót', disable: false },
		{ kind: TermTourismEventSetKinds.DEPARTURE, name: 'Wyjazd', disable: false },
		{ kind: TermTourismEventSetKinds.RETURN, name: 'Powrót', disable: false },
	];
	selectedInsurer: string = null;
	readonly depDate = signal(null);
	readonly retDate = signal(null);
	readonly apiSearchParamURL = computed<string>(() => {
		const params = new HttpParams()
			.set('date_from', this.depDate())
			.set('date_to', this.retDate())
			.set('showDeleted', '0')
			.set('on', '1')
			.set('category', `${EventCategory.OSKAR},${EventCategory.GROUP}`);

		return `${params.toString()}&search`;
	});
	readonly peopleWithFaculties = signal(0);
	readonly peopleFacultiesInfo = computed(() => `Liczba osób z fakultetami: ${this.peopleWithFaculties()}`);
	readonly peopleWithFacultiesExist = computed(() => this.peopleWithFaculties() > 0);
	readonly environment = environment;

	optional = {
		both:
			this.data.maxSeatOptionalReturn === this.data.maxSeatOptionalDeparture
				? this.data.maxSeatOptionalDeparture
				: null,
		departure:
			this.data.maxSeatOptionalReturn !== this.data.maxSeatOptionalDeparture
				? this.data.maxSeatOptionalDeparture
				: null,
		return:
			this.data.maxSeatOptionalReturn !== this.data.maxSeatOptionalDeparture
				? this.data.maxSeatOptionalReturn
				: null,
	};

	product = signal<BagTransportTermProduct>(null);
	readonly TermTourismEventSetKinds = TermTourismEventSetKinds;
	readonly ProductKindType = ProductKindType;

	protected readonly TableName = TableName;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: BagTransportTerm,
		private readonly dateService: DateService,
		private readonly infobox: InfoboxService,
		private readonly loader: LoaderService,
		private readonly dialogRef: MatDialogRef<BagTransportTermDialogComponent>,
		private readonly bagTransportTermApiService: BagTransportTermApiService,
		private readonly cdr: ChangeDetectorRef,
		private readonly downloadFileService: DownloadFileService,
	) {}

	async ngOnInit(): Promise<void> {
		this.depDate.set(this.dateService.addDays(-3, this.data.departureTime));
		this.retDate.set(this.dateService.addDays(3, this.data.returnTime));

		this.loader.show('generateReport');
		await this.getVehicles(this.depDate(), this.retDate());
		if (!this.data.swinging && this.data.products.length === 1) {
			this.product.set(await this.bagTransportTermApiService.getProduct(this.data.products[0]));
		}
		this.selectedInsurer = this.data.insurers?.length ? this.data.insurers[0] : null;
		this.peopleWithFaculties.set(this.data?.peopleWithFaculties ?? 0);
		this.cdr.detectChanges();
	}

	async generateReport(reportType: ReportType, kind: ReportKind): Promise<void> {
		const payload = this.generatePayload(reportType, kind);
		this.loader.show('generateReport');
		const reportResult = await this.bagTransportTermApiService.generateReportFile(payload);
		if (reportResult) {
			await this.fetchReport(`/report/${reportResult.id}/`);
		}
	}

	async downloadReport(reportType: ReportType, kind: ReportKind): Promise<void> {
		try {
			const payload = this.generatePayload(reportType, kind);
			this.loader.show();
			const reportUrl = reportType === 'facultative' ? 'facultative_list' : 'medical_insurance_list';
			if (kind === 'csv') {
				this.bagTransportTermApiService.downloadReportFile(payload, reportUrl).subscribe((blob) => {
					const prefix =
						reportType === 'facultative'
							? FileTypeShortcut.FacultativeList
							: FileTypeShortcut.InsuranceList;
					this.downloadFileService.downloadBlob(blob, this.generateFileName(prefix));
				});
			} else {
				this.bagTransportTermApiService.downloadReportFileUrl(payload, reportUrl).subscribe((result) => {
					window.open(result);
				});
			}
		} catch (err) {
			console.error(err);
		} finally {
			this.loader.hide();
		}
	}

	async patchMaxSeatOptional(): Promise<void> {
		const patchData: Partial<BagTransportTerm> = {
			maxSeatOptionalDeparture: this.data.maxSeatOptionalDeparture,
			maxSeatOptionalReturn: this.data.maxSeatOptionalReturn,
		};

		await this.bagTransportTermApiService.patchTransportTermData(this.data.id, patchData);
	}

	async removeEvent(index: number): Promise<void> {
		try {
			this.loader.show();
			this.data.termtourismeventSet.splice(index, 1);
			await this.saveTourismEvents();
			this.sortTermTourismEventsSets();
		} catch (error) {
			throw new Error(error);
		}
		this.loader.hide();
	}

	async saveTourismEvents(): Promise<void> {
		const payload: Partial<BagTransportTerm> = {
			termtourismeventSet: this.data.termtourismeventSet,
		};
		await this.bagTransportTermApiService.patchTransportTermData(this.data.id, payload);
	}

	async connectTourismEventToTransportTerm(arg: any): Promise<void> {
		this.loader.show();
		try {
			const result = await this.bagTransportTermApiService.getVehicleData(arg.vehicle);
			const eventName = `${arg.name}, ${result['data'].name}`;

			const filteredEvents = this.data.termtourismeventSet.filter((value) => value.eventName === eventName);

			if (this.eventExistsOnDepartureAndReturn(filteredEvents)) {
				this.infobox.warning('Wybrane wydarzenie istnieje już na wyjeździe i powrocie');
				this.loader.hide();
				return;
			}

			if (!arg.allSeatsIsTheSame) {
				this.infobox.warning(
					'To wydarzenie ma kilka przypisanych pojazdów o różnej wartości maksymalnych miejsc, do wydarzenia zostanie przypisana ilość siedzeń z najpóźniej jadącego pojazdu.',
				);
			}

			if (result) {
				const termTourismEventSetObject = {
					id: 0,
					eventId: `${arg.id}`,
					eventName: eventName,
					kind: this.getKindToSet(filteredEvents),
					maxSeat: result['data'].seats + 2,
					crewCount: arg.crewCount || 6,
				};

				const payload: Partial<BagTransportTerm> = {
					termtourismeventSet: [...this.data.termtourismeventSet, termTourismEventSetObject],
				};

				await this.bagTransportTermApiService.patchTransportTermData(this.data.id, payload);
				this.data.termtourismeventSet.push(termTourismEventSetObject);
			}
			this.sortTermTourismEventsSets();
			this.loader.hide();
		} catch (error) {
			throw new Error(error);
		}
	}

	checkOptionsToDisable(tourismEventSet: TermTourismEventSet): void {
		const filteredEvents = this.data.termtourismeventSet.filter((v) => v.eventId === tourismEventSet.eventId);

		const hasDeparture = this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.DEPARTURE);
		const hasReturn = this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.RETURN);
		const hasMultipleEvents = filteredEvents.length > 1;

		this.mappedTourismEventsKinds = this.mappedTourismEventsKinds.map((value): MappedTourismEventsKind => {
			switch (value.kind) {
				case TermTourismEventSetKinds.RETURN:
					return {
						...value,
						disable:
							hasDeparture &&
							tourismEventSet.kind !== TermTourismEventSetKinds.RETURN &&
							hasMultipleEvents,
					};
				case TermTourismEventSetKinds.DEPARTURE:
					return {
						...value,
						disable:
							hasReturn &&
							tourismEventSet.kind !== TermTourismEventSetKinds.DEPARTURE &&
							hasMultipleEvents,
					};
				case TermTourismEventSetKinds.BOTH:
					return { ...value, disable: hasDeparture && hasReturn };
				default:
					return value;
			}
		});
	}

	setMaxSeatOptional(value: number | null, type: TermTourismEventSetKinds): void {
		switch (type) {
			case TermTourismEventSetKinds.BOTH:
				this.optional.both = value;
				this.data.maxSeatOptionalReturn = value;
				this.data.maxSeatOptionalDeparture = value;
				break;
			case TermTourismEventSetKinds.DEPARTURE:
				this.optional.departure = value;
				this.data.maxSeatOptionalDeparture = value;
				break;
			case TermTourismEventSetKinds.RETURN:
				this.optional.return = value;
				this.data.maxSeatOptionalReturn = value;
				break;
			default:
				const neverType: never = type;
				throw new Error(`There is no type like ${neverType}`);
		}
	}

	erpEventsMapper = (response: { data: TourismEventsWithDelay[] }): TourismEventsWithDelay[] => {
		return response.data.map((event) => ({
			...event,
			displayName:
				event.date_from && event.date_to
					? `${event.name} (${this.dateService.apiToDateTime(event.date_from).date} - ${this.dateService.apiToDateTime(event.date_to).date})`
					: event.name,
		}));
	};
	onTourismEventsSelected(event: TourismEventsWithDelay): void {
		this.chosenEvent = {
			id: event.id,
			name: event.name,
			vehicle: event.vehicles.map((v) => v.vehicle_id).pop(),
			crewCount: event.countCrewToSeason,
			dateFrom: this.dateService.apiToDateTime(event.date_from).date,
			dateTo: this.dateService.apiToDateTime(event.date_to).date,
			allSeatsIsTheSame: this.allElementsEqual(
				this.vehicles
					.filter((v) => event.vehicles.map((v) => v.vehicle_id).includes(v.id as string))
					.map((v: VehicleModel) => v.seats),
			),
		};
	}

	private sortTermTourismEventsSets(): void {
		this.data.termtourismeventSet.sort((a, b) => a.eventName.localeCompare(b.eventName));
		this.cdr.detectChanges();
	}

	private eventExistsOnDepartureAndReturn(filteredEvents: TermTourismEventSet[]): boolean {
		return (
			this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.BOTH) ||
			(this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.DEPARTURE) &&
				this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.RETURN))
		);
	}

	private getKindToSet(filteredEvents: TermTourismEventSet[]): TermTourismEventSetKinds {
		if (this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.DEPARTURE)) {
			return TermTourismEventSetKinds.RETURN;
		}

		if (this.checkEventKindExist(filteredEvents, TermTourismEventSetKinds.RETURN)) {
			return TermTourismEventSetKinds.DEPARTURE;
		}

		return TermTourismEventSetKinds.BOTH;
	}

	private async getVehicles(departureDate: string, returnDate: string): Promise<void> {
		const vehicles = await this.bagTransportTermApiService.getVehiclesData();
		this.loader.hide('generateReport');
		if (vehicles) {
			this.vehicles = vehicles;
			const tourismEvents: TourismEventsWithDelay[] = await this.bagTransportTermApiService.getTourismEvents(
				departureDate,
				returnDate,
			);
			if (tourismEvents.length) {
				this.data.termtourismeventSet = this.data.termtourismeventSet.map((bagEvent) => {
					const relatedErpEvent =
						tourismEvents.find((erpEvent) => `${erpEvent.id}` === bagEvent.eventId) ??
						({ name: 'brak' } as TourismEventsWithDelay);

					// needed this to remove error 'This expression is not callable.
					// "error on union of array type' at this.vehicles.find(
					const vehicles: VehicleModel[] | Vehicle[] = [];
					const allVehicles: Array<(typeof vehicles)[number]> = this.vehicles;
					const foundRelatedVehicle: VehicleModel | Vehicle = allVehicles.find(
						(vehicle: Vehicle | VehicleModel) =>
							relatedErpEvent &&
							vehicle &&
							relatedErpEvent?.vehicles?.map((v) => v.vehicle_id).pop() === vehicle.id,
					);

					const relatedVehicle: { name: string } =
						relatedErpEvent && this.vehicles.length && foundRelatedVehicle
							? foundRelatedVehicle
							: { name: 'brak' };

					return {
						...bagEvent,
						eventName: `${relatedErpEvent?.name}, ${relatedVehicle.name}`,
					};
				});
			}
		}
	}

	private checkEventKindExist(tourismEvents: TermTourismEventSet[], kind: TermTourismEventSetKinds): boolean {
		return tourismEvents.some((v) => v.kind === kind);
	}

	private allElementsEqual(arr: unknown[]): boolean {
		if (arr.length === 0) return true;
		return arr.every((el) => el === arr[0]);
	}

	private getOutputParams(kind: ReportKind, prefix: string): GenerateReportPayload['output_params'] {
		const name = this.generateFileName(prefix);
		switch (kind) {
			case 'csv':
				return {
					name,
					kind: 'CSV',
				};
			case 'spreadsheet':
				return {
					name,
					kind: prefix === FileTypeShortcut.InsuranceList ? 'SpreadSheet' : 'Google Drive',
					sheetName: new Date().toLocaleString('pl-PL'),
					path: `Reports/${name}`,
				};
			default:
				// TODO: kind satisfes never if >= 4.9
				const exhaustiveCheck: never = kind;
				throw new Error(`Invalid report kind: ${kind}`);
		}
	}

	private generatePayload(reportType: ReportType, kind: ReportKind): GenerateReportPayload {
		let name: ReportName = 'RoomingListReport';
		let dateLower: string = this.data.accommodation.lower;
		let dateUpper: string = this.data.accommodation.upper;

		if (reportType === 'facultative') {
			const name = this.generateFileName('FL');
			return {
				params: {
					term_id: `${this.data.id}`,
				},
				output_params: {
					kind: kind === 'csv' ? 'CSV' : 'SpreadSheet',
					name,
					sheetName: new Date().toLocaleString('pl-PL'),
					path: `Reports/${name}`,
				},
			};
		}

		if (reportType === 'insurance') {
			name = 'InsuranceListReport';
			if (this.data.departureTime) dateLower = this.data.departureTime;
			if (this.data.returnTime) dateUpper = this.data.returnTime;
		}
		const payload: Omit<GenerateReportPayload, 'output_params'> = {
			name,
			version: 'v1',
			params: {
				term_id: `${this.data.id}`,
				term: {
					lower: dateLower,
					upper: dateUpper,
					bounds: '[)',
				},
				product: this.data.swinging === false ? (this.data.products[0] ?? null) : null,
			},
			insurer: this.selectedInsurer,
		};

		return {
			...payload,
			output_params: this.getOutputParams(
				kind,
				reportType === 'rooming' ? FileTypeShortcut.RoomingList : FileTypeShortcut.InsuranceList,
			),
		};
	}

	private generateFileName(prefix: string): string {
		return `${prefix}_${this.data.departureTime}_${this.data.name}_`.slice(0, MAX_NAME_LENGTH);
	}

	private async fetchReport(url: string, retryCount = 0): Promise<void> {
		const maxRetries = 4;
		const retryDelay = 2000;
		const errorEmpty = 'EE';

		const report = await this.bagTransportTermApiService.getReport(url);
		this.loader.hide('generateReport');
		if (!report) {
			this.closeDialogWithFeedback('Wystąpił nieoczekiwany błąd, spróbuj ponownie później.');
		} else {
			if (report.status === errorEmpty) {
				this.closeDialogWithFeedback('Brak danych w raporcie.');
			} else if (report.url !== null) {
				this.closeDialogWithFeedback('Pomyślnie wygenerowano raport.', true);

				window.open(report.url);
			} else {
				if (retryCount < maxRetries) {
					retryCount++;
					setTimeout(() => this.fetchReport(url, retryCount), retryDelay);
				} else {
					this.closeDialogWithFeedback('Zbyt duża liczba zapytań, spróbuj ponownie później.');
				}
			}
		}
	}

	private closeDialogWithFeedback(message: string, success = false) {
		this.loader.hide('generateReport');
		this.dialogRef.close();
		if (success) {
			this.infobox.success(message);
			return;
		}
		this.infobox.error(message);
	}
}
